/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from '@mui/material/styles';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { FiMaximize2, FiX } from "react-icons/fi";
import defaultCarIcon from "assets/default_car_icon.png";
import notFoundImg from "assets/not_found.png";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiAvatar from "components/VuiAvatar";
import VuiBadge from "components/VuiBadge";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import DriverDetailsModal from "./components/DriverDetailsModal";
import DriverEditModal from "./components/DriverEditModal";

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Create default icon instance
const defaultIcon = L.divIcon({
  className: 'car-marker-icon',
  html: `<img 
    src="${defaultCarIcon}" 
    style="
      width: auto;
      height: 40px;
      object-fit: contain;
    "
    alt="car"
  />`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20]
});

// Create a custom icon class for car markers
const createCarIcon = (iconUrl) => {
  if (!iconUrl) return defaultIcon;
  
  try {
    return L.divIcon({
      className: 'car-marker-icon',
      html: `<img 
        src="${iconUrl}" 
        style="
          width: auto;
          height: 40px;
          object-fit: contain;
        "
        alt="car"
      />`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [0, -20]
    });
  } catch (error) {
    console.error('Error creating car icon:', error);
    return defaultIcon;
  }
};

// Add this CSS to your component's style section
const carMarkerStyles = `
  .car-marker-icon {
    background: none;
    border: none;
  }
  .car-marker-icon img {
    width: auto !important;
    height: 40px !important;
    object-fit: contain;
  }
`;

function Driver() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("1"); // default to active
  const [showModal, setShowModal] = useState(false);
  const [driverDetails, setDriverDetails] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDriverForEdit, setSelectedDriverForEdit] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [selectedDrivers, setSelectedDrivers] = useState(new Set());
  const [highlightedDriver, setHighlightedDriver] = useState(null);
  const mapRef = useRef(null);
  const fullscreenMapRef = useRef(null);
  const { t } = useTranslation();

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.csc-taxi.tech';

  useEffect(() => {
    fetchDrivers();
    // eslint-disable-next-line
  }, [search, active]);

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }
      // Build query params
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('active', active); // always send active, either 1 or 0
      const url = `${API_URL}/api/admin/driver?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError("Oops, we haven't got JSON!");
      }
      const result = await response.json();
      if (result.status === 1) {
        setDrivers(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch drivers');
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setDrivers([]);
      if (error.message.includes('token') || error.message.includes('Session expired')) {
        window.location.href = '/sign-in';
      }
    } finally {
      setLoading(false);
    }
  };

  const openMenu = (event, driver) => {
    setMenu(event.currentTarget);
    setSelectedDriver(driver);
  };

  const closeMenu = () => {
    setMenu(null);
    setSelectedDriver(null);
  };

  const handleEdit = (driver) => {
    setSelectedDriverForEdit(driver);
    setShowEditModal(true);
    closeMenu();
  };

  const handleDelete = (driver) => {
    setDriverToDelete(driver);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/driver/${driverToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      // Refresh the drivers list
      fetchDrivers();
      setDeleteConfirmOpen(false);
      setDriverToDelete(null);
    } catch (error) {
      console.error('Error deleting driver:', error);
      if (error.message.includes('token') || error.message.includes('Session expired')) {
        window.location.href = '/sign-in';
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDriverToDelete(null);
  };

  const handleShowDetails = async (driver) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/driver/${driver.id}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 1) {
        setDriverDetails(result.data);
        setShowModal(true);
      } else {
        throw new Error(result.message || 'Failed to fetch driver details');
      }
    } catch (error) {
      console.error('Error fetching driver details:', error);
      if (error.message.includes('token') || error.message.includes('Session expired')) {
        window.location.href = '/sign-in';
      }
    }
    closeMenu();
  };

  const handleSaveDriver = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/driver/${selectedDriverForEdit.id}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 1) {
        // Refresh the drivers list
        fetchDrivers();
      } else {
        throw new Error(result.message || 'Failed to update driver');
      }
    } catch (error) {
      console.error('Error updating driver:', error);
      if (error.message.includes('token') || error.message.includes('Session expired')) {
        window.location.href = '/sign-in';
      }
      throw error; // Re-throw to let the modal handle the error
    }
  };

  const handleDriverSelect = (driverId) => {
    setSelectedDrivers(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(driverId)) {
        newSelected.delete(driverId);
      } else {
        newSelected.add(driverId);
      }
      return newSelected;
    });

    // Find the selected driver
    const driver = drivers.find(d => d.id === driverId);
    if (driver && driver.current_latitude && driver.current_longitude) {
      const position = [driver.current_latitude, driver.current_longitude];
      // Zoom to driver location in both maps
      if (mapRef.current) {
        mapRef.current.setView(position, 15);
      }
      if (fullscreenMapRef.current) {
        fullscreenMapRef.current.setView(position, 15);
      }
    }
  };

  const clearSelection = () => {
    setSelectedDrivers(new Set());
  };

  const handleMarkerClick = (driverId) => {
    setHighlightedDriver(driverId);
    // Find the row element and scroll it into view
    const rowElement = document.getElementById(`driver-row-${driverId}`);
    if (rowElement) {
      rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Clear highlight after 2 seconds
    setTimeout(() => setHighlightedDriver(null), 2000);
  };

  const columns = [
    { name: "driver", align: "left", label: t('driver_table.driver') },
    { name: "rating", align: "center", label: t('driver_table.rating') },
    { name: "status", align: "center", label: t('driver_table.status') },
    { name: "statistics", align: "center", label: t('driver_table.statistics') },
    { name: "action", align: "center", label: t('driver_table.action') },
  ];

  const rows = drivers.map((driver) => ({
    driver: (
      <VuiBox 
        id={`driver-row-${driver.id}`}
        display="flex" 
        alignItems="center" 
        px={1} 
        py={0.5}
        onClick={() => handleDriverSelect(driver.id)}
        sx={{
          cursor: 'pointer',
          background: selectedDrivers.has(driver.id) 
            ? 'rgba(33, 150, 243, 0.15)' 
            : highlightedDriver === driver.id 
              ? 'rgba(255, 193, 7, 0.15)' 
              : 'transparent',
          borderRadius: '8px',
          transition: 'background 0.2s',
          '&:hover': {
            background: selectedDrivers.has(driver.id) 
              ? 'rgba(33, 150, 243, 0.25)' 
              : highlightedDriver === driver.id 
                ? 'rgba(255, 193, 7, 0.25)' 
                : 'rgba(255, 255, 255, 0.05)',
          },
        }}
      >
        <VuiBox mr={2}>
          <VuiAvatar 
            src={driver.profile_photos?.[0] || driver.profile_photo} 
            alt={`${driver.first_name} ${driver.last_name}`} 
            size="sm" 
            variant="rounded" 
          />
        </VuiBox>
        <VuiBox display="flex" flexDirection="column">
          <VuiTypography variant="button" color="white" fontWeight="medium">
            {`${driver.first_name} ${driver.last_name}`}
          </VuiTypography>
          <VuiTypography variant="caption" color="text" style={{direction:"ltr"}}>
            {driver.phone}
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    ),
    rating: (
      <VuiBox display="flex" flexDirection="column" alignItems="center">
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {(driver.rating || 0).toFixed(1)} ⭐
        </VuiTypography>
        <VuiTypography variant="caption" color="text">
          ({driver.rating_count || 0} {t('driver_table.reviews')})
        </VuiTypography>
      </VuiBox>
    ),
    status: (
      <VuiBadge
        variant="standard"
        badgeContent={driver.active ? t('driver_table.active') : t('driver_table.inactive')}
        color={driver.active ? "success" : "error"}
        size="xs"
        container
        sx={({ palette: { white, success, error }, borders: { borderRadius, borderWidth } }) => ({
          background: driver.active ? success.main : error.main,
          border: `${borderWidth[1]} solid ${driver.active ? success.main : error.main}`,
          borderRadius: borderRadius.md,
          color: white.main,
        })}
      />
    ),
    statistics: (
      <VuiBox display="flex" flexDirection="row" gap={1}>
        <VuiBox
          sx={{
            background: "#e53935", // red
            color: "#fff",
            borderRadius: "8px",
            px: 1.5,
            py: 0.5,
            fontWeight: "bold",
            fontSize: "14px",
            minWidth: "32px",
            textAlign: "center",
          }}
        >
          {driver.statistics.rejected}
        </VuiBox>
        <VuiBox
          sx={{
            background: "#ffb300", // orange
            color: "#fff",
            borderRadius: "8px",
            px: 1.5,
            py: 0.5,
            fontWeight: "bold",
            fontSize: "14px",
            minWidth: "32px",
            textAlign: "center",
          }}
        >
          {driver.statistics.pending}
        </VuiBox>
        <VuiBox
          sx={{
            background: "#43a047", // green
            color: "#fff",
            borderRadius: "8px",
            px: 1.5,
            py: 0.5,
            fontWeight: "bold",
            fontSize: "14px",
            minWidth: "32px",
            textAlign: "center",
          }}
        >
          {driver.statistics.accepted}
        </VuiBox>
      </VuiBox>
    ),
    action: (
      <VuiBox display="flex" flexDirection="row" gap={1}>
        <FaEye
          style={{ color: "#1976d2", cursor: "pointer" }}
          title={t('driver_table.show_details')}
          onClick={() => handleShowDetails(driver)}
        />
        <FaEdit
          style={{ color: "#ffa726", cursor: "pointer" }}
          title={t('driver_table.edit')}
          onClick={() => handleEdit(driver)}
        />
        <FaTrash
          style={{ color: "#e53935", cursor: "pointer" }}
          title= {t('driver_table.delete')}
          onClick={() => handleDelete(driver)}
        />
      </VuiBox>
    ),
  }));

  return (
    <DashboardLayout>
      <style>{carMarkerStyles}</style>
      <DashboardNavbar />
      <VuiBox py={3} minHeight="100vh" display="flex" flexDirection="column" flex={1}>
        <VuiBox mb={3} flex={1} display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={3}>
          {/* Driver Table Card */}
          <Card sx={{ flex: 2, minWidth: 0, overflow: 'hidden', maxHeight: '458px' }}>
            {/* Search and filter controls */}
            <VuiBox display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'center' }} mb={2} px={3}>
              <TextField
                variant="outlined"
                size="small"
                placeholder= {t('driver_table.search')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  style: { background: '#22223a', color: '#fff', borderRadius: 8 },
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControlLabel
                className="green-switch"
                control={
                  <VuiBox display="flex" alignItems="center" gap={1}>
                    <VuiTypography
                      variant="caption"
                      color={active === "0" ? "#43a047" : "#888"}
                      fontWeight={active === "0" ? "bold" : "normal"}
                      sx={{ minWidth: 52, textAlign: 'right' }}
                    >
                      {t('driver_table.inactive')}
                    </VuiTypography>
                    <Switch
                      checked={active === "1"}
                      onChange={e => setActive(e.target.checked ? "1" : "0")}
                      sx={{
                        mx: 1,
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#43a047',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#43a047',
                        },
                      }}
                    />
                    <VuiTypography
                      variant="caption"
                      color={active === "1" ? "#43a047" : "#888"}
                      fontWeight={active === "1" ? "bold" : "normal"}
                      sx={{ minWidth: 40, textAlign: 'left' }}
                    >
                      {t('driver_table.active')}
                    </VuiTypography>
                  </VuiBox>
                }
                sx={{ 
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    marginLeft: 0
                  }
                }}
              />
            </VuiBox>
            {loading ? (
              <VuiBox display="flex" justifyContent="center" p={3}>
                <VuiTypography variant="button" color="white">
                  Loading...
                </VuiTypography>
              </VuiBox>
            ) : drivers.length === 0 ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={5}>
                <img src={notFoundImg} alt="Not found" style={{ maxWidth: 180, opacity: 0.7, marginBottom: 16 }} />
                <VuiTypography variant="button" color="text">
                  No drivers found
                </VuiTypography>
              </VuiBox>
            ) : (
              <VuiBox
                sx={{
                  "& th": {
                    borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                      `${borderWidth[1]} solid ${grey[700]}`,
                    position: 'sticky',
                    top: 0,
                    background: '#191932',
                    zIndex: 2,
                  },
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                        `${borderWidth[1]} solid ${grey[700]}`,
                    },
                  },
                  width: '100%',
                  px: 3,
                }}
              >
                <Table columns={columns} rows={rows} TableContainerProps={{ sx: { maxHeight: '390px' } }} />
              </VuiBox>
            )}
          </Card>

          {/* Map Card */}
          <Card sx={{ flex: 1, height: 'fit-content', minWidth: { lg: '400px' } }}>
            <VuiBox p={2}>
              <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <VuiBox display="flex" alignItems="center" gap={2}>
                  <VuiTypography variant="h6" color="white" fontWeight="bold">
                  {t('driver_table.locations')}
                  </VuiTypography>
                  {selectedDrivers.size > 0 && (
                    <VuiTypography 
                      variant="caption" 
                      color="text"
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { color: '#2196f3' }
                      }}
                      onClick={clearSelection}
                    >
                      Clear Selection ({selectedDrivers.size} selected)
                    </VuiTypography>
                  )}
                </VuiBox>
                <Box
                  sx={{
                    cursor: 'pointer',
                    color: '#fff',
                    background: 'rgba(34,34,58,0.8)',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: '#2196f3',
                      color: '#fff',
                    },
                  }}
                  onClick={() => setMapFullscreen(true)}
                  title="Expand Map"
                >
                  <FiMaximize2 size={20} />
                </Box>
              </VuiBox>
              <Box sx={{ 
                width: '100%', 
                position: 'relative',
                aspectRatio: '1/1',
                maxWidth: { xs: '100%', lg: '600px' },
                margin: '0 auto'
              }}>
                <MapContainer
                  center={[33.5138, 36.2765]}
                  zoom={12}
                  style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                  ref={mapRef}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {drivers.map((driver) => (
                    driver.current_latitude && 
                    driver.current_longitude && 
                    (selectedDrivers.size === 0 || selectedDrivers.has(driver.id)) && (
                      <Marker
                        key={driver.id}
                        position={[driver.current_latitude, driver.current_longitude]}
                        icon={createCarIcon(driver.car_icon)}
                        eventHandlers={{
                          click: () => handleMarkerClick(driver.id)
                        }}
                      >
                        <Popup>
                          <VuiBox>
                            <VuiTypography variant="button" color="text" fontWeight="bold">
                              {driver.first_name} {driver.last_name}
                            </VuiTypography>
                            <VuiTypography variant="caption" color="text" style={{direction:"ltr"}}>
                              {driver.phone}
                            </VuiTypography>
                          </VuiBox>
                        </Popup>
                      </Marker>
                    )
                  ))}
                </MapContainer>
              </Box>
            </VuiBox>
          </Card>
        </VuiBox>
      </VuiBox>
      <Footer />
      <DriverDetailsModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setDriverDetails(null);
        }}
        driver={driverDetails}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      <DriverEditModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedDriverForEdit(null);
        }}
        driver={selectedDriverForEdit}
        onSave={handleSaveDriver}
      />
      {/* Fullscreen Map Dialog */}
      <Dialog
        open={mapFullscreen}
        onClose={() => setMapFullscreen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            background: '#191932',
            color: '#fff',
            borderRadius: '18px',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
            height: '90vh',
            maxHeight: '90vh',
            margin: '20px',
          },
        }}
      >
        <VuiBox p={2} height="100%">
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <VuiTypography variant="h6" color="white" fontWeight="bold">
            {t('driver_table.locations')}
            </VuiTypography>
            <Box
              sx={{
                cursor: 'pointer',
                color: '#fff',
                background: 'rgba(34,34,58,0.8)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                '&:hover': {
                  background: '#e53935',
                  color: '#fff',
                },
              }}
              onClick={() => setMapFullscreen(false)}
              title="Close"
            >
              <FiX size={20} />
            </Box>
          </VuiBox>
          <Box sx={{ height: 'calc(100% - 60px)', width: '100%', position: 'relative' }}>
            <MapContainer
              center={[33.5138, 36.2765]}
              zoom={12}
              style={{ height: '100%', width: '100%', borderRadius: '8px' }}
              ref={fullscreenMapRef}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {drivers.map((driver) => (
                driver.current_latitude && 
                driver.current_longitude && 
                (selectedDrivers.size === 0 || selectedDrivers.has(driver.id)) && (
                  <Marker
                    key={driver.id}
                    position={[driver.current_latitude, driver.current_longitude]}
                    icon={createCarIcon(driver.car_icon)}
                    eventHandlers={{
                      click: () => handleMarkerClick(driver.id)
                    }}
                  >
                    <Popup>
                      <VuiBox>
                        <VuiTypography variant="button" color="text" fontWeight="bold">
                          {driver.first_name} {driver.last_name}
                        </VuiTypography>
                        <VuiTypography variant="caption" color="text" style={{direction:"ltr"}}>
                          {driver.phone}
                        </VuiTypography>
                      </VuiBox>
                    </Popup>
                  </Marker>
                )
              ))}
            </MapContainer>
          </Box>
        </VuiBox>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            background: '#191932',
            color: '#fff',
            borderRadius: '18px',
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Are you sure you want to delete this driver? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleDeleteCancel}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              '&:hover': {
                background: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{
              background: '#d32f2f',
              color: '#ffffff',
              fontWeight: 'bold',
              '&:hover': {
                background: '#b71c1c',
                color: '#ffffff',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Driver; 